import { STRUCTURE_GRID_SIZE } from 'data/constants';
import { GridSpaceDataType } from 'data/types';
import { ZoneConcealedDuctLayout } from 'utils/concealed-duct-geometry';
import {
  clampPointToSpacePolygon,
  clampPointToZonePolygons
} from 'utils/point-clamp';

export type DuctRunKind = 'supply' | 'return';

export type DuctRunRef = {
  zoneUniqueId: string;
  condenserUniqueId?: string;
  kind: DuctRunKind;
  spaceUniqueId?: string;
  runUniqueId: string | null;
};

export type PointPx = { x: number; y: number };

export function ductRunRefKey(ref: DuctRunRef): string {
  const base = `${ref.zoneUniqueId}::${ref.kind}`;
  if (ref.runUniqueId) {
    return `${base}::additional::${ref.runUniqueId}`;
  }
  if (ref.kind === 'supply') {
    return `${base}::primary::${ref.spaceUniqueId}`;
  }
  return `${base}::primary`;
}

export function ductRunEndpointKey(ref: DuctRunRef): string {
  return `${ductRunRefKey(ref)}::endpoint`;
}

export function ductRunWaypointKey(
  ref: DuctRunRef,
  pointUniqueId: string
): string {
  return `${ductRunRefKey(ref)}::waypoint::${pointUniqueId}`;
}

export function buildDuctRunPathPx(
  originPx: PointPx,
  waypointsPx: PointPx[] | null | undefined,
  endpointPx: PointPx
): PointPx[] {
  return [originPx, ...(waypointsPx ?? []), endpointPx];
}

export function snapPxToGrid(px: PointPx): PointPx {
  return {
    x: Math.max(
      0,
      Math.round(px.x / STRUCTURE_GRID_SIZE) * STRUCTURE_GRID_SIZE
    ),
    y: Math.max(0, Math.round(px.y / STRUCTURE_GRID_SIZE) * STRUCTURE_GRID_SIZE)
  };
}

export function clampSupplyEndpointPx(
  proposedPx: PointPx,
  structureSpaceId: string | null,
  gridSpaces: GridSpaceDataType[]
): PointPx | null {
  const room = gridSpaces.find((gs) => gs.spaceId === structureSpaceId);
  if (!room) return null;
  return clampPointToSpacePolygon(proposedPx, room);
}

export function clampReturnEndpointPx(
  proposedPx: PointPx,
  layout: ZoneConcealedDuctLayout,
  gridSpaces: GridSpaceDataType[]
): PointPx {
  const zoneRooms = layout.branches
    .filter((branch) => branch.sameLevelAsOrigin && branch.structureSpaceId)
    .map((branch) =>
      gridSpaces.find((gs) => gs.spaceId === branch.structureSpaceId)
    )
    .filter((room): room is GridSpaceDataType => !!room);
  if (zoneRooms.length === 0) return proposedPx;
  return clampPointToZonePolygons(proposedPx, zoneRooms);
}
